import React from "react";
import { cn } from "@/lib/utils";
import { ExamQuestion } from "@/types/exam";
import { NoCopyQuestionStem } from "@/components/NoCopyQuestionStem";
import { renderFurigana } from "@/lib/japaneseHelpers";

export function getCircleBadge(index: number) {
    const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
    return letters[index] || String.fromCharCode(65 + index);
}

export const isHorizontalLayout = (optionsPairs: { text: string; imageUrl?: string | null; audioUrl?: string | null }[]) => {
    if (optionsPairs.length !== 4) return false;
    const hasMedia = optionsPairs.some(opt => opt.imageUrl || opt.audioUrl);
    if (hasMedia) return false;
    return optionsPairs.every(opt => opt.text.trim().length <= 15);
};

export interface SharedQuestionCardProps {
    q: ExamQuestion;
    questionLabel: string | number;
    marks: number;
    selectedAnswer: number | undefined;
    onSelectAnswer: (questionId: string, origIndex: number) => void;
    optPairs: any[];
    isHighlighted?: boolean;
    topElement?: React.ReactNode;
    audioElement?: React.ReactNode;
    hideImage?: boolean;
}

export const QuestionCard = ({
    q,
    questionLabel,
    marks,
    selectedAnswer,
    onSelectAnswer,
    optPairs,
    isHighlighted,
    topElement,
    audioElement,
    hideImage
}: SharedQuestionCardProps) => {
    const isSelected = (origIndex: number) => selectedAnswer === origIndex;
    const optionType = (q.options as { metadata?: any } | null | undefined)?.metadata?.optionType || "TEXT";

    return (
        <div
            id={`question-card-${q.id}`}
            className={cn(
                "bg-white border-y border-x md:border p-6 md:p-8 shadow-xs flex flex-col rounded-xl transition-all duration-300 scroll-mt-20 relative border-stone-200 hover:border-stone-350",
                isHighlighted
                    ? "border-amber-400 border-l-amber-500 bg-amber-50/10 ring-2 ring-amber-400 shadow-md scale-[1.01]"
                    : ""
            )}
        >
            {/* Header info */}
            <div className="flex items-center justify-between text-xs text-stone-500 border-b border-stone-200 pb-2 mb-4 font-sans select-none">
                <div className="flex items-center gap-2">
                    <span className="font-bold text-stone-850 text-sm font-sans">問 {questionLabel}</span>
                    <span>({marks} 点)</span>
                </div>
            </div>

            <div className="space-y-6">
                {topElement && <div>{topElement}</div>}

                {audioElement && <div className="mb-4 relative z-20">{audioElement}</div>}

                <div className="transition-all duration-300">
                    <NoCopyQuestionStem as="h2" className="text-[1.08rem] font-medium leading-relaxed text-stone-900 mb-3" >
                        {renderFurigana(q.questionText)}
                    </NoCopyQuestionStem>

                    {!hideImage && q.imageUrl && (
                        <img src={q.imageUrl} alt="" className="max-h-[480px] w-full max-w-2xl object-contain my-3 pointer-events-none mx-auto" />
                    )}

                {q.type === "MCQ" || q.type === "LISTENING" ? (
                    (() => {
                        if (optionType === "IMAGE") {
                            return (
                                <div className="grid grid-cols-2 gap-3 mt-4 select-none">
                                    {optPairs.map(({ text, origIndex, imageUrl, audioUrl }, idx) => {
                                        const selected = isSelected(origIndex);
                                        return (
                                            <button
                                                key={`${q.id}-${origIndex}`}
                                                type="button"
                                                onClick={() => onSelectAnswer(q.id, origIndex)}
                                                className={cn(
                                                    "relative flex flex-col items-center justify-center rounded-xl border p-3 aspect-square transition-all duration-200 shadow-xs hover:-translate-y-0.5 hover:shadow-md",
                                                    selected
                                                        ? "border-primary bg-primary/10 text-primary font-semibold shadow-sm -translate-y-0.5 ring-1 ring-primary"
                                                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                                                )}
                                            >
                                                <span className={cn(
                                                    "absolute top-3 left-3 text-sm font-bold select-none leading-none",
                                                    selected ? "text-slate-900" : "text-slate-400"
                                                )}>
                                                    {getCircleBadge(idx)}
                                                </span>
                                                {imageUrl ? (
                                                    <img src={imageUrl} alt="" className="max-h-[85%] max-w-[85%] object-contain pointer-events-none" />
                                                ) : (
                                                    <span className="text-[10px] text-muted-foreground italic">(No Image)</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        }

                        if (optionType === "MIXED") {
                            return (
                                <div className="grid grid-cols-1 gap-3 mt-4 select-none">
                                    {optPairs.map(({ text, origIndex, imageUrl, audioUrl }, idx) => {
                                        const selected = isSelected(origIndex);
                                        return (
                                            <button
                                                key={`${q.id}-${origIndex}`}
                                                type="button"
                                                onClick={() => onSelectAnswer(q.id, origIndex)}
                                                className={cn(
                                                    "w-full text-left rounded-xl border p-4 flex gap-3.5 transition-all duration-200 text-base shadow-xs hover:-translate-y-0.5 hover:shadow-md",
                                                    selected
                                                        ? "border-primary bg-primary/10 text-primary font-semibold shadow-sm -translate-y-0.5 ring-1 ring-primary"
                                                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                                                )}
                                            >
                                                <span className={cn(
                                                    "text-lg shrink-0 select-none leading-none pt-0.5 font-bold transition-colors",
                                                    selected ? "text-primary" : "text-slate-400"
                                                )}>
                                                    {getCircleBadge(idx)}
                                                </span>
                                                <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                                                    <span className="text-[15px] block leading-snug">
                                                        {renderFurigana(text || `Option ${getCircleBadge(idx)}`)}
                                                    </span>
                                                    {imageUrl && (
                                                        <div className="w-16 h-12 shrink-0 overflow-hidden flex items-center justify-center p-0.5">
                                                            <img src={imageUrl} alt="" className="max-h-full max-w-full object-contain pointer-events-none" />
                                                        </div>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        }

                        // Default TEXT/Fallback layout
                        return (
                            <div className={cn(
                                "grid gap-3 mt-3 select-none",
                                isHorizontalLayout(optPairs) ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1 sm:grid-cols-2"
                            )}>
                                {optPairs.map(({ text, origIndex }, idx) => {
                                    const selected = isSelected(origIndex);
                                    return (
                                        <button
                                            key={`${q.id}-${origIndex}`}
                                            type="button"
                                            onClick={() => onSelectAnswer(q.id, origIndex)}
                                            className={cn(
                                                "w-full text-left rounded-xl border p-4 flex gap-3.5 transition-all duration-200 text-base shadow-xs hover:-translate-y-0.5 hover:shadow-md",
                                                selected
                                                    ? "border-primary bg-primary/10 text-primary font-semibold shadow-sm -translate-y-0.5 ring-1 ring-primary"
                                                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                                            )}
                                            
                                        >
                                            <span className={cn(
                                                "text-lg shrink-0 select-none leading-none pt-0.5 font-bold transition-colors",
                                                selected ? "text-slate-900" : "text-slate-400"
                                            )}>
                                                {getCircleBadge(idx)}
                                            </span>
                                            <div className="min-w-0 flex-1 space-y-2">
                                                {text && text.trim().length > 0 && (
                                                    <div className="leading-relaxed text-[15px]">
                                                        {renderFurigana(text)}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })()
                ) : (
                    <p className="text-xs text-stone-400 font-sans">Unsupported question type in Mock CBT.</p>
                )}
                </div>
            </div>
        </div>
    );
};
